import { useCallback, useEffect, useRef } from "react";
import type { LayoutDocument } from "./layout-types";
import type {
  BehaviorGraph,
  LayoutNode,
  SceneCustomEventEntry,
} from "./layout-types";
import type { BehaviorNode } from "../../../util/src/blueprint/types.js";
import {
  normalizeShortcut,
  isControlRoomEventPublishable,
} from "../../../util/src/blueprint/shortcut-keys.js";
import type {
  ControlRoomCapability,
  ControlRoomSignalPayload,
} from "../../../util/src/control-room/protocol.js";
import { CR_EVENT } from "../../../util/src/control-room/protocol.js";
import { behaviorDrainCoordinator } from "./behaviorDrainCoordinator";

type SocketLike = {
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
  emit: (event: string, ...args: unknown[]) => void;
  connected?: boolean;
};

/**
 * Extracts all publishable Control Room capabilities from a layout document.
 */
export function extractControlRoomCapabilities(
  doc: LayoutDocument | undefined | null,
): ControlRoomCapability[] {
  if (!doc?.scene?.children) return [];

  const caps: ControlRoomCapability[] = [];
  const referencedCustomEventIds = new Set<string>();

  const addControlRoomCapability = (bNode: BehaviorNode) => {
    if (
      bNode.type !== "event" ||
      bNode.kind !== "onControlRoom" ||
      !isControlRoomEventPublishable(bNode)
    ) {
      return;
    }

    const combo = normalizeShortcut(
      bNode.shortcutKey1 ?? "",
      bNode.shortcutKey2,
      bNode.shortcutKey3,
    );
    if (!combo) {
      return;
    }

    caps.push({
      combo,
      name: bNode.signalName!,
      description: bNode.signalDescription!,
    });
  };

  const collectBehaviorNodes = (
    behaviorNodes: BehaviorNode[] | undefined,
    options: { publishControlRoom: boolean },
  ) => {
    for (const bNode of behaviorNodes ?? []) {
      if (options.publishControlRoom) {
        addControlRoomCapability(bNode);
      }

      if (
        bNode.type === "event" &&
        bNode.kind === "onCustomSceneEvent" &&
        bNode.customEventId
      ) {
        referencedCustomEventIds.add(bNode.customEventId);
      }
    }
  };

  const walk = (nodes: LayoutNode[]) => {
    for (const node of nodes) {
      collectBehaviorNodes(node.behavior?.nodes as BehaviorNode[] | undefined, {
        publishControlRoom: true,
      });

      if ("children" in node && Array.isArray(node.children)) {
        walk(node.children);
      }
      if (
        "component" in node &&
        node.component === "layoutList" &&
        node.props
      ) {
        const props = node.props as Record<string, unknown>;
        if (props.rowTemplate) walk([props.rowTemplate as LayoutNode]);
        if (Array.isArray(props.rowVariants)) {
          for (const v of props.rowVariants) {
            if ((v as Record<string, unknown>).template) {
              walk([(v as Record<string, unknown>).template as LayoutNode]);
            }
          }
        }
      }
    }
  };

  walk(doc.scene.children);

  const customEvents = doc.scene.catalog?.customEvents ?? [];
  collectReferencedCustomEvents(customEvents, referencedCustomEventIds);

  for (const entry of customEvents) {
    if (!referencedCustomEventIds.has(entry.id)) {
      continue;
    }

    collectBehaviorNodes(
      (entry.triggerGraph as BehaviorGraph | undefined)?.nodes as
        | BehaviorNode[]
        | undefined,
      { publishControlRoom: true },
    );
  }

  return dedupeCapabilities(caps);
}

function collectReferencedCustomEvents(
  customEvents: SceneCustomEventEntry[],
  referencedCustomEventIds: Set<string>,
) {
  let changed = true;

  while (changed) {
    changed = false;

    for (const entry of customEvents) {
      if (!referencedCustomEventIds.has(entry.id)) {
        continue;
      }

      for (const node of entry.triggerGraph.nodes as BehaviorNode[]) {
        if (
          node.type !== "event" ||
          node.kind !== "onCustomSceneEvent" ||
          !node.customEventId ||
          referencedCustomEventIds.has(node.customEventId)
        ) {
          continue;
        }

        referencedCustomEventIds.add(node.customEventId);
        changed = true;
      }
    }
  }
}

function dedupeCapabilities(
  capabilities: ControlRoomCapability[],
): ControlRoomCapability[] {
  return [
    ...new Map(
      capabilities.map((capability) => [
        `${capability.combo}\u0000${capability.name}\u0000${capability.description}`,
        capability,
      ]),
    ).values(),
  ];
}

interface UseControlRoomTargetOptions {
  socket: SocketLike | null | undefined;
  document: LayoutDocument | undefined | null;
  meetId?: string | null;
  targetLabel: string;
  enabled?: boolean;
  onSignal?: (combos: string[]) => void;
}

/**
 * Hook that publishes a target's Control Room capabilities to the hub
 * and listens for incoming shortcut signals.
 *
 * Should be called once per rendered layout target (display or widget).
 */
export function useControlRoomTarget({
  socket,
  document: doc,
  meetId,
  targetLabel,
  enabled = true,
  onSignal,
}: UseControlRoomTargetOptions): void {
  const prevCapsRef = useRef<string>("");

  useEffect(() => {
    if (!socket || !enabled || !meetId) {
      prevCapsRef.current = "";
    }
  }, [socket, enabled, meetId]);

  const publishCapabilities = useCallback(() => {
    if (!socket || !enabled || !meetId) return;
    const caps = extractControlRoomCapabilities(doc);
    const serialized = JSON.stringify({ meetId, caps });
    if (serialized === prevCapsRef.current) return;
    prevCapsRef.current = serialized;
    socket.emit(CR_EVENT.PUBLISH_CAPABILITIES, {
      meetId,
      targetId: "",
      targetLabel,
      capabilities: caps,
    });
  }, [socket, doc, meetId, targetLabel, enabled]);

  useEffect(() => {
    publishCapabilities();
  }, [publishCapabilities]);

  useEffect(() => {
    if (!socket || !enabled || !meetId) return;

    const handleConnect = () => {
      prevCapsRef.current = "";
      publishCapabilities();
    };

    socket.on("connect", handleConnect);
    return () => {
      socket.off("connect", handleConnect);
    };
  }, [socket, enabled, meetId, publishCapabilities]);

  useEffect(() => {
    if (!socket || !enabled || !meetId) return;

    const handleSignal = (...args: unknown[]) => {
      const payload = args[0] as ControlRoomSignalPayload | undefined;
      if (!payload?.combos?.length || payload.meetId !== meetId) return;
      if (onSignal) {
        onSignal(payload.combos);
        return;
      }

      behaviorDrainCoordinator.dispatchControlRoomSignals(payload.combos);
    };

    socket.on(CR_EVENT.SIGNAL_SHORTCUT, handleSignal);
    return () => {
      socket.off(CR_EVENT.SIGNAL_SHORTCUT, handleSignal);
    };
  }, [socket, enabled, meetId, onSignal]);
}
